import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { StrictMode, useEffect, useRef, useState } from "react";
import { createRoot } from "react-dom/client";
import {
  BrowserRouter,
  Link,
  Navigate,
  NavLink,
  Outlet,
  Route,
  Routes,
  useLocation,
  useNavigate,
} from "react-router";
import { ApiError, api, onAuthError } from "./api.ts";
import { HostGuard, onAppHost } from "./hosts.ts";
import { ActivityAdmin } from "./pages/activity.tsx";
import { AiAdmin } from "./pages/ai.tsx";
import { MfaSetup, MfaVerify, SignIn, SignUp } from "./pages/auth.tsx";
import { ClaimDetail, ClaimsList } from "./pages/claims.tsx";
import { ConnectClaude } from "./pages/claude.tsx";
import { ContractReview, ContractsList } from "./pages/contracts.tsx";
import { Help } from "./pages/help.tsx";
import { Home } from "./pages/home.tsx";
import { Landing } from "./pages/landing.tsx";
import { PeriodPage } from "./pages/period.tsx";
import { Settings } from "./pages/settings.tsx";
import { VendorDetail, VendorsList } from "./pages/vendors.tsx";
import { WaitlistAdmin } from "./pages/waitlist.tsx";
import { applyTheme, type Me, useMe } from "./session.ts";
import {
  BlogIndex,
  EntryPage,
  GetStarted,
  HowItWorks,
  PostPage,
  Pricing,
  SectionIndex,
  SiteHome,
  VendorPage,
  VendorsIndex,
} from "./site/site.tsx";
import { replayTour, Tour } from "./tour.tsx";
import "./styles.css";
import { DevicePasskeyOffer } from "./device-passkey.tsx";
import { StepUpDialog } from "./stepup.tsx";
import { ButtonLink, buttonClass, Card, cx, Empty, Loading, navItemClass, Page } from "./ui.tsx";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (n, e) => !(e instanceof ApiError && e.status < 500) && n < 2,
      refetchOnWindowFocus: false,
    },
  },
});

applyTheme();

/** Routes session failures to the right step, from any request. */
function AuthRouter() {
  const navigate = useNavigate();
  useEffect(
    () =>
      onAuthError((e) => {
        queryClient.invalidateQueries({ queryKey: ["me"] });
        if (e.code === "signed_out") navigate("/signin");
        if (e.code === "mfa_setup_required") navigate("/mfa/setup");
        if (e.code === "mfa_required") navigate("/mfa/verify");
      }),
    [navigate],
  );
  return null;
}

function RequireMember() {
  const { data: me, isLoading, isFetching } = useMe();
  const loc = useLocation();
  // Right after signing in, the cached answer can still say "signed out" while the fresh one
  // loads; wait for it rather than sending the person back to sign in.
  if (isLoading || (isFetching && !me?.user)) return <Loading />;
  if (!me?.user)
    // Visitors to the front door see the marketing page; deep links still go to sign-in.
    // At the app's own address, its front door is sign-in; the site lives at ninekeep.com.
    return loc.pathname === "/" && !onAppHost() ? (
      // Campaign links carry tags and go to the focused landing page; everyone else sees the site.
      loc.search.includes("utm_") ? (
        <Navigate to={`/welcome${loc.search}`} replace />
      ) : (
        <SiteHome />
      )
    ) : (
      <Navigate
        to={`/signin?next=${encodeURIComponent(loc.pathname + loc.search + loc.hash)}`}
        replace
      />
    );
  if (!me.mfa.enabled) return <Navigate to="/mfa/setup" replace />;
  if (!me.mfa.verified) return <Navigate to="/mfa/verify" replace />;
  return <Shell me={me} />;
}

const NAV = [
  { to: "/", label: "Home", end: true },
  { to: "/claims", label: "Claims" },
  { to: "/vendors", label: "Vendors" },
  { to: "/contracts", label: "Contracts" },
];

const initials = (name: string) =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]!.toUpperCase())
    .join("");

/** Top right on every page: who is signed in, their settings, and the way out. */
function AccountMenu({ me, compact }: { me: Me; compact?: boolean }) {
  const [open, setOpen] = useState(false);
  const [leaving, setLeaving] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const button = useRef<HTMLButtonElement>(null);
  const loc = useLocation();
  const navigate = useNavigate();
  // biome-ignore lint/correctness/useExhaustiveDependencies: close on navigation
  useEffect(() => setOpen(false), [loc.pathname]);
  useEffect(() => {
    if (!open) return;
    const away = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    };
    const esc = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      setOpen(false);
      button.current?.focus();
    };
    document.addEventListener("mousedown", away);
    document.addEventListener("keydown", esc);
    return () => {
      document.removeEventListener("mousedown", away);
      document.removeEventListener("keydown", esc);
    };
  }, [open]);
  const name = me.user?.name || me.user?.email || "";
  const logOut = async () => {
    setLeaving(true);
    try {
      await api.post("/auth/logout");
    } finally {
      // Nothing of this account stays in memory for whoever uses the browser next.
      queryClient.clear();
      navigate("/signin", { replace: true });
    }
  };
  const item = "block w-full rounded-chip px-3 py-2 text-left text-sm hover:bg-surface-2";
  return (
    <div ref={ref} className="relative">
      <button
        ref={button}
        type="button"
        className="flex items-center gap-2 rounded-control border border-line bg-surface px-1.5 py-1 text-sm hover:bg-surface-2"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        aria-controls="account-menu"
        aria-label={`Account: ${name}`}
      >
        <span className="grid size-7 place-items-center rounded-chip bg-accent-soft text-xs font-semibold text-accent">
          {initials(name) || "?"}
        </span>
        {!compact && <span className="max-w-48 truncate pr-1 font-medium">{name}</span>}
        <span aria-hidden className="pr-1 text-xs text-muted">
          ▾
        </span>
      </button>
      {open && (
        <div
          id="account-menu"
          className="absolute right-0 z-30 mt-2 w-64 rounded-card border border-line bg-surface p-1.5 shadow-pop"
        >
          <div className="px-3 py-2">
            <div className="truncate text-sm font-semibold">{name}</div>
            <div className="truncate text-xs text-muted">{me.user?.email}</div>
            {me.org && <div className="truncate text-xs text-faint">{me.org.name}</div>}
          </div>
          <div className="my-1 border-t border-line" />
          <Link to="/settings" className={item}>
            Settings
          </Link>
          <Link to="/help" className={item}>
            Help
          </Link>
          <button
            type="button"
            className={item}
            onClick={() => {
              setOpen(false);
              replayTour();
            }}
          >
            Take the tour
          </button>
          <div className="my-1 border-t border-line" />
          <button
            type="button"
            className={cx(item, "text-danger")}
            disabled={leaving}
            onClick={logOut}
          >
            {leaving ? "Logging out…" : "Log out"}
          </button>
        </div>
      )}
    </div>
  );
}

function Shell({ me }: { me: Me }) {
  const [open, setOpen] = useState(false);
  const loc = useLocation();
  // biome-ignore lint/correctness/useExhaustiveDependencies: close the menu on navigation
  useEffect(() => {
    setOpen(false);
  }, [loc.pathname]);
  const nav = (
    <nav aria-label="Main" className="flex h-full flex-col gap-1 p-3">
      {/* On a phone the bar above already shows the name. */}
      <Link
        to="/"
        aria-label="ninekeep home"
        className="mb-4 hidden items-center gap-2 rounded-control px-3 pt-2 md:flex"
      >
        <img src="/favicon.svg" alt="" className="size-6" />
        <span className="font-display text-lg font-bold tracking-tight">ninekeep</span>
      </Link>
      {NAV.map((n) => (
        <NavLink key={n.to} to={n.to} end={n.end} className={navItemClass}>
          {n.label}
        </NavLink>
      ))}
      <div className="mt-auto flex flex-col gap-1 border-t border-line pt-3">
        <NavLink to="/settings" className={navItemClass}>
          Settings
        </NavLink>
        <NavLink to="/help" className={navItemClass}>
          Help
        </NavLink>
        {me.operator && (
          <>
            <NavLink to="/team/activity" className={navItemClass}>
              Activity
            </NavLink>
            <NavLink to="/team/waitlist" className={navItemClass}>
              Waitlist
            </NavLink>
            <NavLink to="/team/ai" className={navItemClass}>
              AI
            </NavLink>
          </>
        )}
      </div>
    </nav>
  );
  return (
    <div className="min-h-dvh md:grid md:grid-cols-[232px_1fr]">
      <a href="#main" className="skip-link">
        Skip to content
      </a>
      <aside className="sticky top-0 hidden h-dvh border-r border-line bg-surface md:block">
        {nav}
      </aside>
      <div className="sticky top-0 z-20 flex items-center justify-between border-b border-line bg-surface px-4 py-3 md:hidden">
        <Link to="/" aria-label="ninekeep home" className="flex items-center gap-2">
          <img src="/favicon.svg" alt="" className="size-6" />
          <span className="font-display font-bold">ninekeep</span>
        </Link>
        <div className="flex items-center gap-2">
          <AccountMenu me={me} compact />
          <button
            type="button"
            className={buttonClass("secondary")}
            onClick={() => setOpen((o) => !o)}
            aria-expanded={open}
            aria-controls="mobile-nav"
          >
            {open ? "Close" : "Menu"}
          </button>
        </div>
      </div>
      {open && (
        <div id="mobile-nav" className="border-b border-line bg-surface md:hidden">
          {nav}
        </div>
      )}
      <div className="min-w-0">
        <header className="sticky top-0 z-20 hidden h-14 items-center justify-end border-b border-line bg-surface/90 px-8 backdrop-blur md:flex">
          <AccountMenu me={me} />
        </header>
        <main id="main" tabIndex={-1} className="focus:outline-none">
          <Outlet />
          <Tour me={me} />
        </main>
      </div>
    </div>
  );
}

/** An address that isn't a page: say so, and offer the way home instead of a silent jump. */
function NotFound() {
  return (
    <Page help="first-result" title="Page not found">
      <Card>
        <Empty title="There's no page at this address.">
          <div className="mt-3 flex justify-center gap-2">
            <ButtonLink to="/">Go to Home</ButtonLink>
            <ButtonLink to="/claims">See claims</ButtonLink>
          </div>
        </Empty>
      </Card>
    </Page>
  );
}

function App() {
  return (
    <BrowserRouter>
      <HostGuard />
      <AuthRouter />
      <StepUpDialog />
      <DevicePasskeyOffer />
      <Routes>
        <Route path="/welcome" element={<Landing audience="company" />} />
        <Route path="/how-it-works" element={<HowItWorks />} />
        <Route path="/pricing" element={<Pricing />} />
        <Route path="/waitlist" element={<GetStarted />} />
        <Route path="/get-started" element={<GetStarted />} />
        <Route path="/supported-vendors" element={<VendorsIndex />} />
        <Route path="/resources" element={<BlogIndex />} />
        <Route path="/resources/:slug" element={<PostPage />} />
        <Route path="/supported-vendors/:slug" element={<VendorPage />} />
        {(["product", "solutions", "industries", "customers"] as const).flatMap((k) => [
          <Route key={k} path={`/${k}`} element={<SectionIndex k={k} />} />,
          <Route key={`${k}/:slug`} path={`/${k}/:slug`} element={<EntryPage k={k} />} />,
        ])}
        <Route path="/partners" element={<Landing audience="partner" />} />
        <Route path="/signin" element={<SignIn />} />
        <Route path="/signup" element={<SignUp />} />
        <Route path="/mfa/setup" element={<MfaSetup />} />
        <Route path="/mfa/verify" element={<MfaVerify />} />
        <Route element={<RequireMember />}>
          <Route index element={<Home />} />
          <Route path="/claims" element={<ClaimsList />} />
          <Route path="/claims/:id" element={<ClaimDetail />} />
          <Route path="/vendors" element={<VendorsList />} />
          <Route path="/vendors/:id" element={<VendorDetail />} />
          <Route path="/months/:id" element={<PeriodPage />} />
          <Route path="/contracts" element={<ContractsList />} />
          <Route path="/contracts/:id" element={<ContractReview />} />
          <Route path="/settings" element={<Settings />} />
          <Route path="/connect/claude" element={<ConnectClaude />} />
          <Route path="/help" element={<Help />} />
          <Route path="/team/activity" element={<ActivityAdmin />} />
          <Route path="/team/waitlist" element={<WaitlistAdmin />} />
          <Route path="/team/ai" element={<AiAdmin />} />
          {/* Old addresses */}
          <Route path="/incidents" element={<Navigate to="/vendors?tab=incidents" replace />} />
          <Route path="/recovered" element={<Navigate to="/claims?tab=recovered" replace />} />
          <Route path="/documents/*" element={<Navigate to="/contracts" replace />} />
          <Route path="*" element={<NotFound />} />
        </Route>
      </Routes>
    </BrowserRouter>
  );
}

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <App />
    </QueryClientProvider>
  </StrictMode>,
);
